import { Checkbox, FormControlLabel, List, ListItem, ListItemText } from "@mui/material";
import styles from "./EventInterestsSkillsSection.module.scss";
import { getNestedSkillName, getNestedInterestName } from "../../utils/enumHelpers";

interface SubItem {
  interest_id?: string | number;
  skill_id?: string | number;
  interest_name_vol?: string;
  interest_name_org?: string;
  skill_name_vol?: string;
  skill_name_org?: string;
  name?: string;
}

interface Category {
  id: string | number;
  name: string;
  interests?: SubItem[];
  skills?: SubItem[];
}

interface EventInterestsSkillsSectionProps {
  title: string;
  items?: Category[];
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  editMode?: boolean;
  userType?: "volunteer" | "organisation";
  showSubItems?: boolean;
  type?: "interests" | "skills";
}

export const EventInterestsSkillsSection = ({
  title,
  items = [],
  selectedIds = [],
  onToggle,
  editMode = false,
  userType = "volunteer",
  showSubItems = false,
  type = "interests",
}: EventInterestsSkillsSectionProps) => {
  // Helper function to find category by ID
  const findCategoryById = (categoryId: string): Category | null => {
    const found = items.find((category) => String(category.id) === categoryId);
    return found || null;
  };

  // Get the correct name property based on user type using utility functions
  const getSubItemName = (subItem: SubItem): string => {
    if (type === "interests") {
      return getNestedInterestName(subItem, userType);
    } else if (type === "skills") {
      return getNestedSkillName(subItem, userType);
    }
    return subItem.name || '';
  };

  if (editMode) {
    if (!onToggle) {
      console.warn("EventInterestsSkillsSection: onToggle is required for edit mode");
      return null;
    }
    // Edit mode - show checkboxes for categories with optional sub-items
    return (
      <div className={styles.container}>
        <h4>{title}</h4>
        <div className={styles.group}>
          {items?.length > 0 &&
            items?.map((category) => (
              <div key={category.id} className={styles.categoryGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIds?.some(id => id === String(category.id))}
                      onChange={() => onToggle(String(category.id))}
                      name={String(category.id)}
                    />
                  }
                  label={category.name}
                />
                {showSubItems && category[type]?.map((subItem) => (
                  <p key={subItem[`${type === "interests" ? "interest" : "skill"}_id` as keyof SubItem] as string | number} className={styles.subItem}>
                    {getSubItemName(subItem)}
                  </p>
                ))}
              </div>
            ))}
        </div>
      </div>
    );
  }

  // View mode - show list of categories
  return (
    <div className={styles.container}>
      <h4>{title}</h4>
      <List className={styles.list}>
        {selectedIds?.map((categoryId, idx) => {
          const foundCategory = findCategoryById(categoryId);
          return (
            <ListItem key={idx} disablePadding className={styles.item}>
              <ListItemText primary={foundCategory?.name || categoryId} />
            </ListItem>
          );
        })}
      </List>
    </div>
  );
};

